import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { BlockThemeConfig, Service } from 'webapp-types';
import { BlockRenderStep, BlockVisualization } from 'webapp/enums/enums';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

import type BlockRenderer from 'webapp/models-custom/block-renderer';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type BlockState from 'webapp/models-custom/block-state';
import DChart from 'webapp/d-chart/d-chart';

interface Args {
	blockState: BlockState;
	blockRenderer: BlockRenderer;
	themeData: BlockThemeConfig.BlockParsed;
	canAnnotate: boolean;
}

export default class BlockContentChartComponent extends Component<Args> {
	@service('navigation') declare navigationService: Service.Navigation;

	node: HTMLElement | null = null;
	dChart: DChart | null = null;

	get block() {
		return this.args.blockState.block;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization() {
		return this.blockSetup.visualization;
	}

	get annotationContext() {
		return this.args.blockState.annotationContext;
	}

	get sizeModifierClass() {
		const size = this.args.blockRenderer.blockFontSize < 7 ? 'small' : 'normal';
		return `size--${size}`;
	}

	@action
	resetChartPointerPosition() {
		this.dChart?.resetPointerPosition();
	}

	@action
	onThemeChange(): void {
		this.dChart?.updateTheme(this.args.themeData);
	}

	@action
	onCustomColorChange(): void {
		this.dChart?.updateCustomColors(this.blockSetup.customColorClassMap);
	}

	@action
	setupView(node: HTMLElement): void {
		this.node = node;
		const blockRenderer = this.args.blockRenderer;
		blockRenderer.registerStepHandler(BlockRenderStep.CHART, this, (changes) => {
			if (changes) {
				if (!this.dChart) {
					if (blockRenderer.dataReadyForDChart) {
						this.createDChart(node);
					}
					return Promise.resolve();
				}

				if (changes.visualization) {
					this.dChart?.updateVisualization(this.visualization);
				}
				if (changes.data || changes.visSettingsHash) {
					this.dChart?.updateData();
				}
				if (
					changes.font ||
					changes.visualization ||
					changes.blockInnerHeight ||
					changes.blockInnerWidth ||
					changes.blockFontSize ||
					changes.titleHeight ||
					changes.numbersHeight
				) {
					this.resizeChart();
				}
			}
			return Promise.resolve();
		});

		this.node.addEventListener('blockPositionChanged', this.resetChartPointerPosition);

		// update block inner sizes just once more
		blockRenderer.updateBlockInnerSize();

		if (blockRenderer.dataReadyForDChart) {
			// delay initializing of the chart until we have sample
			this.createDChart(node);
		}

		// prepare functions for teardown, since they dont have access to this.args
		this.teardownFns.push(() => {
			blockRenderer.unregisterStepHandler(BlockRenderStep.CHART, this);
		});
	}

	private teardownFns: (() => void)[] = [];

	@action
	teardownView(node: HTMLElement | null): void {
		this.teardownFns.forEach((fn) => fn());

		if (this.dChart) {
			this.dChart.teardown();
		}
		// null when the modifier is destroyed before it was installed
		node?.removeEventListener('blockPositionChanged', this.resetChartPointerPosition);
	}

	private createDChart(node: HTMLElement) {
		const width = this.getChartWidth(),
			maxHeight = this.getMaxChartHeight(width);

		const opts = {
			node,
			visualization: this.visualization,
			maxWidth: width,
			maxHeight: maxHeight,
			sizeModifier: this.args.blockRenderer.blockFontSize,
			themeData: this.args.themeData,
			canAnnotate: this.args.canAnnotate,
			isAnimated: !this.navigationService.isLoadedFromSnapshooter,
			annotationContext: this.annotationContext!,
			customColorClassMap: this.blockSetup.customColorClassMap,
		};

		this.dChart = new DChart(opts);
	}

	private resizeChart() {
		// get space for chart
		const width = this.getChartWidth(),
			height = this.getMaxChartHeight(width);

		if (this.dChart) {
			this.dChart?.setSize(width, height, this.args.blockRenderer.blockFontSize);
		}
	}

	private getChartWidth(): number {
		let width = 0;
		// some charts have negative margins
		const leftMargin = parseFloat(window.getComputedStyle(this.node!).marginLeft);
		if (leftMargin < 0) {
			width = this.args.blockRenderer.getBlockOuterWidth() || 0;
		} else {
			width = this.args.blockRenderer.blockInnerWidth;
		}
		return Math.max(0, width);
	}

	private getMaxChartHeight(width: number): number {
		const node = this.node;

		if (!node) {
			return 0;
		}

		// get all available space
		const blockRenderer = this.args.blockRenderer,
			allHeight = blockRenderer.blockInnerHeight,
			topNodeHeight = blockRenderer.getTopContentHeight(),
			blockTitleHeight = blockRenderer.titleHeight,
			blockNumbersHeight = blockRenderer.numbersHeight;

		const availableHeight = allHeight - topNodeHeight - blockTitleHeight - blockNumbersHeight;

		let height = Math.min(availableHeight, this.getMaxHeight(width));

		if (height < 0) {
			height = 0;
		}

		node.style.height = `${height}px`;

		return height;
	}

	private getMaxHeight(width: number): number {
		let height = Infinity;

		// only important for those that do not stretch
		switch (this.visualization.visualizationType) {
			case BlockVisualization.GAUGE: {
				height = (1.2 * width) / 2;
				break;
			}
		}

		return height;
	}
	<template>
		<div
			class="bc__chart {{this.sizeModifierClass}}"
			{{didInsert this.setupView}}
			{{didUpdate this.onThemeChange @themeData}}
			{{didUpdate this.onCustomColorChange this.blockSetup.customColorClassMap}}
			{{willDestroy this.teardownView}}
		>
		</div>
	</template>
}
